import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaArrowLeft, FaHistory, FaDollarSign, FaCheck, FaTimes as FaTimesIcon, FaExchangeAlt, FaFileAlt, FaChevronDown, FaChevronRight, FaUserCheck } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface TaskHistoryEntry {
  id: string;
  taskId: string;
  projectId: string;
  businessId: string;
  businessName: string;
  action: string;
  data: any;
  timestamp: string;
  createdAt: string;
  performedBy: {
    uid: string;
    email: string;
    role: string;
  };
}

const TaskHistory: React.FC = () => {
  const { taskId, projectId } = useParams();
  const { userData } = useAuth();
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [taskInfo, setTaskInfo] = useState<any>(null);
  const [filter, setFilter] = useState('all');
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const { t } = useTranslation();

  const actionTypes = ['offer_sent', 'offer_accepted', 'offer_rejected', 'counter_offer', 'worker_removed', 'worker_negotiation'];

  useEffect(() => {
    if (taskId) {
      fetchTaskHistory();
      fetchTaskInfo();
    }
  }, [taskId]);

  const fetchTaskHistory = async () => {
    try {
      setLoading(true);
      if (!projectId || !userData || !userData.businessId) throw new Error('Faltan datos de usuario o proyecto');
      const q = query(
        collection(db, `projects/${userData.businessId}/projects/${projectId}/history`),
        where('taskId', '==', taskId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const entries: TaskHistoryEntry[] = [];
      
      querySnapshot.forEach((doc) => {
        entries.push({ id: doc.id, ...doc.data() } as TaskHistoryEntry);
      });
      
      setHistory(entries);
    } catch (error) {
      console.error('Error fetching task history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskInfo = async () => {
    if (!userData?.businessId || !projectId || !taskId) {
      setTaskInfo({ id: taskId, title: 'Tarea no encontrada' });
      return;
    }
    try {
      const taskDocRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks`);
      // Consulta directa por ID de documento
      const docSnap = await getDocs(query(taskDocRef, where('__name__', '==', taskId)));
      if (!docSnap.empty) {
        const taskData = docSnap.docs[0].data();
        setTaskInfo({ id: taskId, title: taskData.title || 'Sin título' });
      } else {
        setTaskInfo({ id: taskId, title: 'Tarea no encontrada' });
      }
    } catch (error) {
      setTaskInfo({ id: taskId, title: 'Error al cargar tarea' });
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'offer_sent':
        return <FaDollarSign className="text-blue-500" />;
      case 'offer_accepted':
        return <FaCheck className="text-green-500" />;
      case 'offer_rejected':
        return <FaTimesIcon className="text-red-500" />;
      case 'counter_offer':
        return <FaExchangeAlt className="text-yellow-500" />;
      case 'worker_removed':
        return <FaTimesIcon className="text-red-600" />;
      case 'worker_negotiation':
        return <FaExchangeAlt className="text-purple-500" />;
      default:
        return <FaHistory className="text-gray-500" />;
    }
  };

  const getActionLabel = (action: string) => {
    return t(`taskHistory.${action}`, action);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'offer_sent':
        return 'bg-blue-100 text-blue-800';
      case 'offer_accepted':
        return 'bg-green-100 text-green-800';
      case 'offer_rejected':
        return 'bg-red-100 text-red-800';
      case 'counter_offer':
        return 'bg-yellow-100 text-yellow-800';
      case 'worker_removed':
        return 'bg-red-100 text-red-800';
      case 'worker_negotiation':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleAccordion = (index: number) => {
    setOpenIndexes(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const filteredHistory = history.filter(entry => {
    if (filter === 'all') return true;
    return entry.action === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header sticky glassmorphism */}
        <div className="sticky top-0 z-20 bg-white/60 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 mb-8 px-8 py-6 flex items-center justify-between gap-4 animate-fadeIn">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 text-gray-600 hover:text-red-600 bg-white/70 rounded-full shadow border border-gray-200 transition-colors"
              title={t('common.back', 'Back')}
            >
              <FaArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3 tracking-tight">
                <FaHistory className="text-red-500" /> {t('taskHistory.title')}
              </h1>
              <p className="text-gray-700 mt-1 text-lg font-semibold">
                {t('taskHistory.task')}: {taskInfo?.title || taskId}
              </p>
            </div>
          </div>
        </div>
        {/* Filtros sticky glassmorphism */}
        <div className="sticky top-[110px] z-10 bg-white/70 backdrop-blur-xl rounded-xl shadow border border-white/30 mb-4 px-8 py-3 flex flex-wrap gap-2 items-center animate-fadeIn">
          <span className="text-gray-700 font-medium mr-2">{t('taskHistory.filterByAction')}</span>
          <button
            className={`px-3 py-1 rounded-full text-sm font-semibold border transition-colors shadow-sm ${filter === 'all' ? 'bg-red-600 text-white border-red-600' : 'bg-white/80 text-gray-700 border-gray-300 hover:bg-red-50'}`}
            onClick={() => setFilter('all')}
          >
            {t('taskHistory.all')}
          </button>
          {actionTypes.map(type => (
            <button
              key={type}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-colors shadow-sm ${filter === type ? 'bg-red-600 text-white border-red-600' : 'bg-white/80 text-gray-700 border-gray-300 hover:bg-red-50'}`}
              onClick={() => setFilter(type)}
            >
              {t(`taskHistory.${type}`)}
            </button>
          ))}
        </div>
        {/* Accordion visual mejorado */}
        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 overflow-hidden animate-fadeIn">
          <div className="px-8 py-6 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 tracking-wide flex items-center gap-2">
              <FaFileAlt className="text-red-400" /> {t('taskHistory.records')} ({filteredHistory.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredHistory.map((entry, idx) => {
              const isOpen = openIndexes.includes(idx);
              return (
                <div key={entry.id} className={`transition-all duration-300 ${isOpen ? 'bg-white/90' : 'bg-white/60 hover:bg-red-50'}` }>
                  <button
                    className={`w-full flex items-center justify-between px-8 py-5 transition-colors focus:outline-none group ${isOpen ? 'border-l-4 border-red-500' : ''}`}
                    onClick={() => toggleAccordion(idx)}
                  >
                    <div className="flex flex-col items-start gap-1 flex-1 text-left">
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold shadow-sm ${getActionColor(entry.action)}`}>
                          {getActionIcon(entry.action)}
                          <span className="ml-2">{getActionLabel(entry.action)}</span>
                        </span>
                        <span className="text-xs text-gray-500 font-mono ml-2">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs text-gray-700 mt-1 italic group-hover:text-red-600 transition-colors">{entry.data?.summary || '-'}</span>
                    </div>
                    <span className={`ml-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>{isOpen ? <FaChevronDown /> : <FaChevronRight />}</span>
                  </button>
                  {isOpen && (
                    <div className="px-10 pb-8 pt-2 bg-gradient-to-br from-white/90 via-red-50 to-white/80 rounded-b-2xl animate-fadeIn shadow-inner border-t border-red-100">
                      <div className="text-sm text-gray-900 mb-2 flex items-center gap-2">
                        <FaUserCheck className="text-red-300" />
                        <strong>{t('taskHistory.performedBy')}</strong> {entry.performedBy.email}
                      </div>
                      <div className="text-xs text-gray-700">
                        {entry.action === 'task_created' && entry.data.task ? (
                          <div className="space-y-1">
                            <div><b>{t('projects.kanban.taskTitle')}:</b> {entry.data.task.title}</div>
                            <div><b>{t('projects.kanban.taskDescription')}:</b> {entry.data.task.description || '-'}</div>
                            <div><b>{t('projects.kanban.status')}:</b> {entry.data.task.columnId}</div>
                            <div><b>{t('projects.kanban.startDate')}:</b> {entry.data.task.startDate ? (entry.data.task.startDate.seconds ? new Date(entry.data.task.startDate.seconds * 1000).toLocaleDateString() : entry.data.task.startDate) : '-'}</div>
                            <div><b>{t('projects.kanban.dueDate')}:</b> {entry.data.task.dueDate ? (entry.data.task.dueDate.seconds ? new Date(entry.data.task.dueDate.seconds * 1000).toLocaleDateString() : entry.data.task.dueDate) : '-'}</div>
                            <div><b>{t('projects.kanban.duration')}:</b> {entry.data.task.duration || '-'} {entry.data.task.durationUnit || ''}</div>
                            <div><b>{t('projects.kanban.progress')}:</b> {entry.data.task.progress != null ? `${entry.data.task.progress}%` : '-'}</div>
                          </div>
                        ) : (
                          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(entry.data, null, 2)}</pre>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredHistory.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📜</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('taskHistory.noHistory')}</h3>
                <p className="text-gray-500">{t('taskHistory.noHistory')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskHistory; 